/** Thin fetch wrapper. Errors carry the API's own message (FastAPI's `detail`),
 *  which is written for the person using the app, so it is shown as-is. */
export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (res.status === 401 && !path.startsWith("/auth/")) {
    // The session ended (expired, revoked, or the server restarted). Tell the
    // app so it can show the sign-in screen instead of a wall of errors.
    window.dispatchEvent(new CustomEvent("brinley:signed-out"));
  }
  if (!res.ok) {
    const detail = data?.detail;
    const msg = typeof detail === "string" ? detail
      : Array.isArray(detail) ? detail.map((d: { loc?: string[]; msg: string }) =>
          `${(d.loc ?? []).slice(1).join(".")}: ${d.msg}`).join("; ")
      : `${method} ${path} returned ${res.status}`;
    throw new ApiError(res.status, msg);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body: unknown = {}) => request<T>("POST", path, body),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  del: (path: string) => request<void>("DELETE", path),
};

/** Fired after any change to the task list, so the menu's count follows. */
export const TASKS_CHANGED = "brinley:tasks-changed";

export interface User {
  id: number; email: string; name: string; role: "admin" | "user"; active: boolean;
  must_change_password: boolean; last_login_at: string | null; created_at: string;
  last_active_at?: string | null;   // only on the administrator's people list
}

export interface SessionRow {
  id: number; current: boolean; created_at: string; last_seen_at: string;
  expires_at: string; ip: string | null; user_agent: string | null;
}

// ---- shared shapes -----------------------------------------------------------
export type Severity = "info" | "warning" | "error";
export type DocType = "sales_invoice" | "transfer_invoice" | "purchase_order";
export type DocStatus = "draft" | "issued" | "shipped" | "received" | "cancelled";

export interface Issue {
  id: number; check: string; severity: Severity; summary: string; detail: Record<string, unknown>;
  detected_at: string; resolved_at: string | null; source: string | null; source_name?: string | null;
  run_id: number | null; document_id?: number | null;
}

export interface Party {
  id: number; code: string; name: string; kind: string; address: string | null; attn: string | null;
  phone: string | null; email: string | null; reference_ids: Record<string, string>; notes: string | null;
  customer_id: number | null; customer: string | null; location: string | null; active: boolean;
}

export interface DocLine {
  line_no?: number; sku: string | null; item_name?: string | null; description: string;
  qty: number; uom: string; unit_price: number; extension?: number;
}

export interface DocSummary {
  id: number; doc_type: DocType; type_label: string; number: string; status: DocStatus;
  issue_date: string | null; ship_date: string | null; counterparty: string | null;
  total_qty: number; total_amount: number; currency: string; lines: number; imported: boolean;
  practice: boolean;
}

export interface PartyBlock { id: number; name: string; address: string | null; attn: string | null; phone: string | null; email: string | null }

export interface DocDetail extends Omit<DocSummary, "lines"> {
  required_date: string | null;
  issuer_id: number | null; bill_to_id: number | null; ship_to_id: number | null;
  consignee_id: number | null; vendor_id: number | null;
  parties: Record<string, PartyBlock | null>;
  customer: { id: number; name: string } | null; channel: string | null;
  from_location: string | null; to_location: string | null;
  customer_po: string | null; booking_no: string | null; ship_via: string | null; origin: string | null;
  pallets: number | null; weight_lbs: number | null; banner: string | null; bottled_by: string | null;
  remarks: string | null; notes: string | null; header_fields: { label: string; value: string }[];
  lines: DocLine[]; transitions: DocStatus[]; editable: boolean;
  events: { at: string; action: string; detail: string | null }[];
  movements: { sku: string | null; cases: number; shipped: string | null; received: string | null; from: string | null; to: string | null }[];
  issues: { id: number; severity: Severity; summary: string; resolved: boolean }[];
  original_file: string | null; has_original: boolean;
}
